import { ref, computed, watch, onBeforeUnmount } from "vue";

/**
 * Auto-fetching composable that handles reactive data loading
 * Components never need to worry about data loading - it's automatic!
 *
 * @param {Object} store - The Pinia store instance
 * @param {Function} getId - Function that returns the current ID to fetch
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive data, loading, and error states
 */
export function useAutoFetch(store, getId, options = {}) {
  const {
    getterMethod = "getById",
    fetchMethod = "fetchById",
    dependencies = [],
    immediate = true,
  } = options;

  // Track if we've initiated fetching for this ID
  const fetchedIds = ref(new Set());
  const isLocalLoading = ref(false);

  // Main reactive data - automatically fetches when accessed
  const data = computed(() => {
    const id = getId();
    if (!id) return null;

    // Get data from store (always check store first)
    const existingData = store[getterMethod](id);

    // If data exists, return it
    if (existingData) {
      return existingData;
    }

    // If data doesn't exist and we haven't fetched it yet, trigger fetch
    if (!fetchedIds.value.has(id) && !isLocalLoading.value) {
      fetchData(id);
    }

    return null; // Return null while loading
  });

  // Auto-fetch data when needed
  const fetchData = async (id) => {
    if (!id || fetchedIds.value.has(id)) return;

    try {
      isLocalLoading.value = true;
      fetchedIds.value.add(id);

      console.log(`AutoFetch: Loading ${store.$id} item ${id}`);
      await store[fetchMethod](id);

      // Handle dependencies (e.g., fetch related data)
      for (const dep of dependencies) {
        const depData = store[getterMethod](id);
        if (depData && dep.condition(depData)) {
          await dep.fetch(depData);
        }
      }
    } catch (error) {
      console.error(`AutoFetch: Error loading ${store.$id} item ${id}:`, error);
      // Remove from fetched set so we can retry later
      fetchedIds.value.delete(id);
    } finally {
      isLocalLoading.value = false;
    }
  };

  // Watch for ID changes and auto-fetch
  if (immediate) {
    watch(
      getId,
      (newId, oldId) => {
        if (newId && newId !== oldId) {
          fetchData(newId);
        }
      },
      { immediate: true },
    );
  }

  // Cleanup on unmount
  onBeforeUnmount(() => {
    fetchedIds.value.clear();
  });

  return {
    data,
    loading: computed(() => store.loading || isLocalLoading.value),
    error: computed(() => store.error),
    refetch: () => {
      const id = getId();
      if (id) {
        fetchedIds.value.delete(id);
        fetchData(id);
      }
    },
  };
}

/**
 * Auto-fetch a single item by ID from any store
 * Ultra-simple API for components
 */
export function useAutoFetchById(store, idRef, options = {}) {
  return useAutoFetch(
    store,
    () => (typeof idRef === "function" ? idRef() : idRef.value),
    options,
  );
}

/**
 * Auto-fetch report with automatic account loading
 * Handles the common pattern of report + account
 */
export function useAutoFetchReport(reportsStore, accountsStore, reportIdRef) {
  const report = useAutoFetchById(reportsStore, reportIdRef, {
    dependencies: [
      {
        condition: (reportData) => reportData?.account_id,
        fetch: async (reportData) => {
          if (
            reportData.account_id &&
            !accountsStore.getAccountById(reportData.account_id)
          ) {
            await accountsStore.fetchById(reportData.account_id);
          }
        },
      },
    ],
  });

  const account = computed(() => {
    return report.data.value?.account_id
      ? accountsStore.getAccountById(report.data.value.account_id)
      : null;
  });

  return {
    report: report.data,
    account,
    loading: computed(() => report.loading.value || accountsStore.loading),
    error: computed(() => report.error.value || accountsStore.error),
    refetch: report.refetch,
  };
}
